import React, { useMemo, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@nextui-org/react";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import useSWR from "swr";
import ClientDataGrid from "../../../components/elements/ClientDataGrid";
import ControlledInput from "../../../components/elements/ControlledInput";
import ControlledReactSelect from "../../../components/elements/ControlledReactSelect";
import RoleAccess from "../../../enums/roles";
import useListCompany from "../../../hooks/useListCompany";
import { BASE_URL, getFetcher, postFetcher } from "../../../services/api";
import RootAdmin from "../../../components/layouts/RootAdmin";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

export default function UseReviewPage() {
  const { data, isLoading, mutate } = useSWR(
    `/api/users/search-review-users?statusRegistrasi=false&returnAll=true`,
    getFetcher
  );
  const [isOpenModalForm, setIsOpenModalForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Tambahkan state untuk loading submit
  const columns = useMemo(
    () => [
        { field: "kodePermohonan", headerName: "Kode Permohonan",},
      { field: "fullName", headerName: "Nama Pendaftar",
      valueGetter: (value, row)  => row?.user?.fullName,
      renderCell: (params) => (
        params?.row?.user?.fullName
      ), },
      { field: "email", headerName: "Email Pendaftar",
      valueGetter: (value, row)  => row?.user?.email,
      renderCell: (params) => (
        params?.row?.user?.email
      ), },
      { field: "name", headerName: "Nama Perusahaan",
      valueGetter: (value, row)  => row?.company?.name,
      renderCell: (params) => (
        params?.row?.company?.name
      ), },
      { field: "npwp", headerName: "NPWP Perusahaan",
      valueGetter: (value, row)  => row?.company?.npwp,
      renderCell: (params) => (
        params?.row?.company?.npwp
      ), },
      { field: "alamatKantor", headerName: "Alamat Perusahaan",
      valueGetter: (value, row)  => row?.company?.alamatKantor,
      renderCell: (params) => (
        params?.row?.company?.alamatKantor
      ), },
      {
        field: 'lampiranFormulir',
        headerName: 'Lampiran Formulir', // Pastikan selalu ada nilai fallback
        renderCell: (params) => {
            const buktiUrl = `${BASE_URL}/${params?.row?.fileUrl}`;
            return params?.row?.fileUrl? (
                <a 
                    href={buktiUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-primary-500 underline"
                >
                    Lihat Lampiran Formulir
                </a>
            ) : (
                <span className="text-ink-500">Tidak ada lampiran formulir</span> // Placeholder jika URL tidak ada
            );
        },
    },
      { field: "namePenanggungJawab", headerName: "Nama Penanggung Jawab",
      valueGetter: (value, row)  => row?.company?.PenanggungJawabPerusahaan[0]?.jabatan,
      renderCell: (params) => (
        params?.row?.company?.PenanggungJawabPerusahaan[0]?.jabatan
      ), },
      { field: "jabatan", headerName: "Jabatan Penanggung Jawab",
      valueGetter: (value, row)  => row?.company?.PenanggungJawabPerusahaan[0]?.jabatan,
      renderCell: (params) => (
        params?.row?.company?.PenanggungJawabPerusahaan[0]?.jabatan
      ), },
      {
        field: "actions",
        headerName: "Aksi",
        renderCell: (params) => (
          <Button
            color="primary"
            isIconOnly
            size="sm"
            onPress={() => handleEditUser(params.row)}
          >
            <PencilSquareIcon className="size-4" />
          </Button>
        ),
      },
    ],
    []
  );

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsOpenModalForm(true);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
    setIsOpenModalForm(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true); // Set loading state saat proses submit dimulai
    try {
      await postFetcher(`/api/users/review-user`, {
        id: selectedUser.id,
        statusRegistrasi: true, // Set to true for approval
      });
      toast.success("Berhasil review user");
      mutate(); // Refresh data
      setIsOpenModalForm(false);
      setIsSubmitting(true); // Set loading state saat proses submit dimulai
    } catch (error) {
      toast.error("Error reviewing user:", error);
    }
  };

  return (
    <RootAdmin>
      <Card radius="sm">
        <CardHeader>Daftar Review Pengguna</CardHeader>
        <Divider />
        <CardBody>
          <div className="h-[500px]">
            <ClientDataGrid
              rows={data?.data || []}
              rowCount={data?.total || 0}
              isLoading={isLoading}
              columns={columns}
            />
          </div>
        </CardBody>
      </Card>

      {isOpenModalForm && (
        <Modal
          size="lg"
          isOpen={isOpenModalForm}
          onOpenChange={setIsOpenModalForm}
        >
          <ModalContent>
            <ModalHeader>Review User</ModalHeader>
            <ModalBody>
            <form onSubmit={handleSubmit}>
                <Input
                    label="Nama Lengkap"
                    name="fullName"
                    className="mt-4"
                    value={selectedUser?.user?.fullName || ""}
                    disabled
                />
                <Input
                    label="Email"
                    name="email"
                    className="mt-4"
                    value={selectedUser?.user?.email || ""}
                    disabled
                />

                {/* Tambahan Detail Perusahaan */}
                <div className="mt-4">
                    <h4 className="text-lg font-semibold">Detail Perusahaan</h4>
                    <Input
                    label="Nama Perusahaan"
                    name="companyName"
                    className="mt-4"
                    value={selectedUser?.company?.name || "Tidak tersedia"}
                    disabled
                    />
                    <Input
                    label="NPWP Perusahaan"
                    name="companyNPWP"
                    className="mt-4"
                    value={selectedUser?.company?.npwp || "Tidak tersedia"}
                    disabled
                    />
                    <Input
                    label="Alamat Kantor"
                    name="companyAddress"
                    className="mt-4"
                    value={selectedUser?.company?.alamatKantor || "Tidak tersedia"}
                    disabled
                    />
                    <Input
                    label="Nama Penanggung Jawab"
                    name="responsiblePersonName"
                    className="mt-4"
                    value={selectedUser?.company?.PenanggungJawabPerusahaan[0]?.nama || "Tidak tersedia"}
                    disabled
                    />
                    <Input
                    label="Jabatan Penanggung Jawab"
                    name="responsiblePersonPosition"
                    className="mt-4"
                    value={selectedUser?.company?.PenanggungJawabPerusahaan[0]?.jabatan || "Tidak tersedia"}
                    disabled
                    />
                </div>

                {/* Tombol Submit */}
                <div className="flex items-center gap-2 mt-4">
                    <Button type="submit" color="primary">
                    Approve
                    </Button>
                    <Button color="danger" onPress={handleCloseModal}>
                    Batal
                    </Button>
                </div>
                </form>

            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </RootAdmin>
  );
}